import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Button } from '../../../../components/atoms/button/button';
import { DateInput } from '../../../../components/atoms/date-input/date-input';
import { Input } from '../../../../components/atoms/input/input';
import { Textarea } from '../../../../components/atoms/textarea/textarea';
import { ClientesMultiSelect } from '../../../../components/molecules/clientes-multi-select/clientes-multi-select';
import { Drawer } from '../../../../components/molecules/drawer/drawer';
import {
  AudienciaAviso,
  AUDIENCIA_AVISO_DICA,
  AUDIENCIA_AVISO_LABEL,
  AvisoDetail,
  CreateAvisoPayload,
  TipoAviso,
  TIPO_AVISO_DICA,
  TIPO_AVISO_LABEL,
  UpdateAvisoPayload,
} from '../../../../types/aviso';
import { toDateInputValue, toIsoFromDateInput } from '../format';
import { AvisoBannerPreview } from './aviso-banner-preview';
import {
  AvisoFormErrors,
  AvisoFormValues,
  hasErrors,
  validateAvisoForm,
} from './validation';

type Mode = 'create' | 'edit';

interface Props {
  isOpen: boolean;
  mode: Mode;
  aviso?: AvisoDetail | null;
  saving?: boolean;
  errorMessage?: string | null;
  onSubmit: (payload: CreateAvisoPayload | UpdateAvisoPayload) => void;
  onClose: () => void;
}

const Tabs = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const TabButton = styled.button<{ $active: boolean }>`
  background: transparent;
  border: none;
  border-bottom: 2px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.brandPrimary : 'transparent'};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.textPrimary : theme.colors.textSecondary};
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  padding: 10px 18px;
  cursor: pointer;
  font-size: 14px;
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const FieldError = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.danger};
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.bgMuted};
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;

  input {
    margin: 0;
  }

  span small {
    display: block;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const DatesRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const ErrorAlert = styled.div`
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.dangerBg};
  border-left: 3px solid ${({ theme }) => theme.colors.danger};
  color: ${({ theme }) => theme.colors.danger};
  font-size: 13px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const PreviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const PreviewMeta = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const initialValues = (aviso?: AvisoDetail | null): AvisoFormValues => ({
  titulo: aviso?.titulo ?? '',
  mensagem: aviso?.mensagem ?? '',
  tipo: aviso?.tipo ?? '',
  dataInicio: toDateInputValue(aviso?.dataInicio ?? ''),
  dataExpiracao: toDateInputValue(aviso?.dataExpiracao ?? ''),
  audiencia: aviso?.audiencia ?? AudienciaAviso.TODOS,
  destinatariosIds: aviso?.destinatariosIds ?? [],
});

export const AvisoFormDrawer = ({
  isOpen,
  mode,
  aviso,
  saving,
  errorMessage,
  onSubmit,
  onClose,
}: Props) => {
  const [values, setValues] = useState<AvisoFormValues>(initialValues(aviso));
  const [errors, setErrors] = useState<AvisoFormErrors>({});
  const [activeTab, setActiveTab] = useState<'form' | 'preview'>('form');

  useEffect(() => {
    if (!isOpen) return;
    setValues(initialValues(aviso));
    setErrors({});
    setActiveTab('form');
  }, [isOpen, aviso]);

  const isDirty = useMemo(() => {
    const initial = initialValues(aviso);
    const sameIds =
      values.destinatariosIds.length === initial.destinatariosIds.length &&
      values.destinatariosIds.every((id) =>
        initial.destinatariosIds.includes(id),
      );
    return (
      values.titulo !== initial.titulo ||
      values.mensagem !== initial.mensagem ||
      values.tipo !== initial.tipo ||
      values.dataInicio !== initial.dataInicio ||
      values.dataExpiracao !== initial.dataExpiracao ||
      values.audiencia !== initial.audiencia ||
      !sameIds
    );
  }, [values, aviso]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleChange = <K extends keyof AvisoFormValues>(
    field: K,
    value: AvisoFormValues[K],
  ) => {
    const next = { ...values, [field]: value };
    setValues(next);
    setErrors(validateAvisoForm(next));
  };

  const handleSubmit = () => {
    const next = validateAvisoForm(values);
    setErrors(next);
    if (hasErrors(next)) return;

    const payload: CreateAvisoPayload = {
      titulo: values.titulo.trim(),
      mensagem: values.mensagem.trim(),
      tipo: values.tipo as TipoAviso,
      dataInicio: toIsoFromDateInput(values.dataInicio),
      dataExpiracao: toIsoFromDateInput(values.dataExpiracao),
      audiencia: values.audiencia,
      destinatariosIds:
        values.audiencia === AudienciaAviso.ESPECIFICOS
          ? values.destinatariosIds
          : [],
    };
    onSubmit(payload);
  };

  const submitDisabled =
    saving || hasErrors(errors) || (mode === 'edit' && !isDirty);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      width={720}
      title={mode === 'create' ? 'Novo aviso' : 'Editar aviso'}
      description={
        mode === 'create'
          ? 'Avisos só ficam visíveis dentro da janela de datas'
          : `Editando ${aviso?.titulo ?? ''}`.trim()
      }
      footer={
        <Footer>
          <Button variant="ghost" onClick={handleClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={submitDisabled}
            loading={saving}
            data-testid="aviso-form-submit"
          >
            Salvar
          </Button>
        </Footer>
      }
    >
      {errorMessage && <ErrorAlert>{errorMessage}</ErrorAlert>}

      <Tabs role="tablist">
        <TabButton
          role="tab"
          aria-selected={activeTab === 'form'}
          $active={activeTab === 'form'}
          onClick={() => setActiveTab('form')}
          data-testid="aviso-form-tab-formulario"
        >
          Formulário
        </TabButton>
        <TabButton
          role="tab"
          aria-selected={activeTab === 'preview'}
          $active={activeTab === 'preview'}
          onClick={() => setActiveTab('preview')}
          data-testid="aviso-form-tab-previa"
        >
          Prévia
        </TabButton>
      </Tabs>

      {activeTab === 'form' && (
        <Form>
          <Field>
            <FieldLabel htmlFor="aviso-titulo">Título *</FieldLabel>
            <Input
              id="aviso-titulo"
              data-testid="aviso-input-titulo"
              maxLength={120}
              placeholder="Ex.: Manutenção programada de saques"
              value={values.titulo}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleChange('titulo', e.target.value)
              }
              hasError={Boolean(errors.titulo)}
            />
            {errors.titulo && <FieldError>{errors.titulo}</FieldError>}
          </Field>

          <Field>
            <FieldLabel htmlFor="aviso-mensagem">Mensagem *</FieldLabel>
            <Textarea
              id="aviso-mensagem"
              data-testid="aviso-input-mensagem"
              maxLength={2000}
              rows={4}
              placeholder="Texto exibido aos usuários da plataforma"
              value={values.mensagem}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                handleChange('mensagem', e.target.value)
              }
              hasError={Boolean(errors.mensagem)}
            />
            {errors.mensagem && <FieldError>{errors.mensagem}</FieldError>}
          </Field>

          <Field>
            <FieldLabel>Tipo *</FieldLabel>
            <RadioGroup>
              {[TipoAviso.GERAL, TipoAviso.FINANCEIRO].map((tipo) => (
                <RadioOption key={tipo}>
                  <input
                    type="radio"
                    name="aviso-tipo"
                    value={tipo}
                    checked={values.tipo === tipo}
                    onChange={() => handleChange('tipo', tipo)}
                    data-testid={`aviso-radio-tipo-${tipo}`}
                  />
                  <span>
                    {TIPO_AVISO_LABEL[tipo]}
                    <small>{TIPO_AVISO_DICA[tipo]}</small>
                  </span>
                </RadioOption>
              ))}
            </RadioGroup>
            {errors.tipo && <FieldError>{errors.tipo}</FieldError>}
          </Field>

          <DatesRow>
            <Field>
              <FieldLabel htmlFor="aviso-inicio">Data de início *</FieldLabel>
              <DateInput
                id="aviso-inicio"
                data-testid="aviso-input-data-inicio"
                value={values.dataInicio}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleChange('dataInicio', e.target.value)
                }
                hasError={Boolean(errors.dataInicio)}
              />
              {errors.dataInicio && (
                <FieldError>{errors.dataInicio}</FieldError>
              )}
            </Field>
            <Field>
              <FieldLabel htmlFor="aviso-fim">Data de expiração *</FieldLabel>
              <DateInput
                id="aviso-fim"
                data-testid="aviso-input-data-expiracao"
                value={values.dataExpiracao}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleChange('dataExpiracao', e.target.value)
                }
                hasError={Boolean(errors.dataExpiracao)}
              />
              {errors.dataExpiracao && (
                <FieldError>{errors.dataExpiracao}</FieldError>
              )}
            </Field>
          </DatesRow>

          <Field>
            <FieldLabel>Audiência *</FieldLabel>
            <RadioGroup>
              {[AudienciaAviso.TODOS, AudienciaAviso.ESPECIFICOS].map(
                (aud) => (
                  <RadioOption key={aud}>
                    <input
                      type="radio"
                      name="aviso-audiencia"
                      value={aud}
                      checked={values.audiencia === aud}
                      onChange={() => handleChange('audiencia', aud)}
                      data-testid={`aviso-radio-audiencia-${aud}`}
                    />
                    <span>
                      {AUDIENCIA_AVISO_LABEL[aud]}
                      <small>{AUDIENCIA_AVISO_DICA[aud]}</small>
                    </span>
                  </RadioOption>
                ),
              )}
            </RadioGroup>
          </Field>

          {values.audiencia === AudienciaAviso.ESPECIFICOS && (
            <Field>
              <FieldLabel>Destinatários *</FieldLabel>
              <ClientesMultiSelect
                value={values.destinatariosIds}
                onChange={(next) => handleChange('destinatariosIds', next)}
                selectedHints={aviso?.destinatarios}
                hasError={Boolean(errors.destinatariosIds)}
                testIdPrefix="aviso-destinatarios"
              />
              {errors.destinatariosIds && (
                <FieldError>{errors.destinatariosIds}</FieldError>
              )}
            </Field>
          )}
        </Form>
      )}

      {activeTab === 'preview' && (
        <PreviewWrapper>
          <PreviewMeta>
            Esta é uma simulação de como o aviso aparecerá na plataforma.
          </PreviewMeta>
          {values.tipo ? (
            <AvisoBannerPreview
              tipo={values.tipo as TipoAviso}
              titulo={values.titulo}
              mensagem={values.mensagem}
            />
          ) : (
            <PreviewMeta>
              Selecione um tipo no formulário para visualizar a prévia.
            </PreviewMeta>
          )}
        </PreviewWrapper>
      )}
    </Drawer>
  );
};
